/**
 * React Query hooks for notifications
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  Notification,
  NotificationsResponse
} from '../api/notifications';
import { toast } from 'sonner';

// ==================== Query Keys ====================

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (unreadOnly?: boolean) => [...notificationKeys.all, 'list', unreadOnly] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

// ==================== Hooks ====================

/**
 * Hook to fetch notifications
 */
export function useNotifications(
  unreadOnly: boolean = false,
  options?: Omit<UseQueryOptions<NotificationsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: notificationKeys.list(unreadOnly),
    queryFn: () => getNotifications(unreadOnly),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refresh every 60 seconds
    ...options
  });
}

/**
 * Hook to fetch unread notification count
 */
export function useUnreadNotificationCount(
  options?: Omit<UseQueryOptions<{ unread_count: number }>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: getUnreadCount,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Auto-refresh every minute
    ...options
  });
}

/**
 * Hook to mark notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => markNotificationRead(notificationId),
    onSuccess: () => {
      // Invalidate notifications list
      queryClient.invalidateQueries({ 
        queryKey: notificationKeys.all 
      });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to mark notification as read');
    }
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ 
        queryKey: notificationKeys.all 
      });
      toast.success('All notifications marked as read');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to mark all as read');
    }
  });
}

/**
 * Hook to delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => deleteNotification(notificationId),
    onSuccess: () => {
      // Invalidate notifications list
      queryClient.invalidateQueries({ 
        queryKey: notificationKeys.all 
      });
      toast.success('Notification deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete notification');
    }
  });
}